"use client"

import { useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CheckCircle2, FileText, AlertCircle, Clock } from "lucide-react"
import type { VisaProcessStepWithDocuments } from "@/lib/types/visa"

export function VisaProcessTimeline({ steps }: { steps: VisaProcessStepWithDocuments[] }) {
    const [selectedStep, setSelectedStep] = useState<VisaProcessStepWithDocuments | null>(null)

    return (
        <>
            <div className="space-y-4">
                {steps.map((step, idx) => (
                    <Card key={step.id} className="relative hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                            <div className="flex gap-4">
                                {/* Step Number */}
                                <div className="flex flex-col items-center">
                                    <div className="h-10 w-10 shrink-0 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                                        {step.step_order}
                                    </div>
                                    {idx < steps.length - 1 && (
                                        <div className="w-0.5 flex-1 bg-muted my-2" />
                                    )}
                                </div>

                                {/* Step Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-4 mb-2">
                                        <div>
                                            <h4 className="font-bold text-lg">{step.title}</h4>
                                            {step.short_description && (
                                                <p className="text-sm text-muted-foreground mt-1">{step.short_description}</p>
                                            )}
                                        </div>
                                        {step.estimated_duration_days && (
                                            <Badge variant="outline" className="shrink-0">
                                                <Clock className="h-3 w-3 mr-1" />
                                                ~{step.estimated_duration_days} gün
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Document Count */}
                                    {step.documents && step.documents.length > 0 && (
                                        <div className="flex items-center gap-2 mt-3">
                                            <FileText className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm text-muted-foreground">
                                                {step.documents.filter(d => d.is_mandatory).length} zorunlu, {" "}
                                                {step.documents.filter(d => !d.is_mandatory).length} opsiyonel belge
                                            </span>
                                        </div>
                                    )}

                                    {/* Popup Tip */}
                                    {step.popup_tip_title && (
                                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex gap-2">
                                            <AlertCircle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-semibold text-yellow-800">{step.popup_tip_title}</p>
                                                <p className="text-xs text-yellow-700 mt-1">{step.popup_tip_body}</p>
                                            </div>
                                        </div>
                                    )}

                                    <Button
                                        variant="link"
                                        className="mt-3 p-0 h-auto"
                                        onClick={() => setSelectedStep(step)}
                                    >
                                        Detayları Gör →
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Step Detail Dialog */}
            <Dialog open={!!selectedStep} onOpenChange={() => setSelectedStep(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Adım {selectedStep?.step_order}: {selectedStep?.title}</DialogTitle>
                    </DialogHeader>

                    {selectedStep && (
                        <div className="space-y-6">
                            {/* Detailed Instructions */}
                            {selectedStep.detailed_instructions && (
                                <div className="prose prose-sm max-w-none">
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: selectedStep.detailed_instructions
                                                .replace(/\n/g, '<br />')
                                                .replace(/##\s(.+)/g, '<h3 class="font-bold text-lg mt-4 mb-2">$1</h3>')
                                                .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                                                .replace(/- (.+)/g, '<li>$1</li>')
                                        }}
                                    />
                                </div>
                            )}

                            {/* Documents */}
                            {selectedStep.documents && selectedStep.documents.length > 0 && (
                                <div>
                                    <h4 className="font-bold text-lg mb-3">Gerekli Belgeler</h4>
                                    <div className="space-y-2">
                                        {selectedStep.documents.map((doc) => (
                                            <div key={doc.id} className="p-4 border rounded-lg">
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h5 className="font-semibold">{doc.document_name}</h5>
                                                            {doc.is_mandatory && (
                                                                <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Zorunlu</Badge>
                                                            )}
                                                        </div>
                                                        {doc.description && (
                                                            <p className="text-sm text-muted-foreground">{doc.description}</p>
                                                        )}
                                                        {doc.country_specific_note && (
                                                            <p className="text-xs text-blue-600 mt-2 italic">
                                                                ℹ️ {doc.country_specific_note}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {doc.sample_file_url && (
                                                        <Button variant="outline" size="sm" asChild>
                                                            <a href={doc.sample_file_url} target="_blank" rel="noopener noreferrer">
                                                                Örnek İndir
                                                            </a>
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedStep.popup_tip_body && (
                                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                                    <p className="text-sm font-semibold text-yellow-800 mb-1">
                                        ⚠️ {selectedStep.popup_tip_title}
                                    </p>
                                    <p className="text-sm text-yellow-700">{selectedStep.popup_tip_body}</p>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}
